import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { PawPrint, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const ComingSoonModal = ({ room, open, onClose, onSubscribe }) => {
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (open) {
      setEmail("");
      setError("");
      setDone(false);
      setLoading(false);
    }
  }, [open, room?.id]);

  if (!room) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    const value = email.trim();
    if (!value) {
      setError("Please enter your email address.");
      return;
    }
    if (!EMAIL_RE.test(value)) {
      setError("That email doesn't look quite right — mind checking it?");
      return;
    }
    setError("");
    setLoading(true);
    try {
      await onSubscribe(value);
      setDone(true);
      toast("You're on the list");
    } catch (err) {
      setError("Something went wrong on our end — please try again in a moment.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        data-testid="coming-soon-modal"
        className="max-w-[calc(100vw-2rem)] gap-0 rounded-[28px] border-none p-8 shadow-[0_40px_90px_-30px_rgba(42,37,30,0.5)] sm:max-w-md sm:p-10"
        style={{
          background: `linear-gradient(180deg, ${room.accent}1f 0%, rgba(249,247,241,0) 52%), #F9F7F1`,
        }}
      >
        <AnimatePresence mode="wait" initial={false}>
          {done ? (
            <motion.div
              key="success"
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
              className="flex flex-col items-center py-4 text-center"
              data-testid="success-message"
            >
              <span
                className="flex h-14 w-14 items-center justify-center rounded-full"
                style={{ backgroundColor: `${room.accent}26` }}
              >
                <Check className="h-6 w-6" style={{ color: room.accent }} />
              </span>
              <DialogTitle className="mt-6 font-serif text-3xl font-medium tracking-tight text-ink">
                You&rsquo;re on the list.
              </DialogTitle>
              <DialogDescription className="mt-3 text-sm leading-relaxed text-ink/70">
                We&rsquo;ll let you know when {room.name} opens.
              </DialogDescription>
              <button
                type="button"
                data-testid="back-to-home-button"
                onClick={onClose}
                className="mt-8 inline-flex h-12 items-center justify-center rounded-full px-8 text-[13px] font-semibold uppercase tracking-[0.16em] text-ivory transition-transform duration-300 hover:scale-[1.03] active:scale-[0.98]"
                style={{ backgroundColor: room.accent }}
              >
                Back to browsing
              </button>
            </motion.div>
          ) : (
            <motion.div
              key="form"
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
            >
              <span
                className="flex h-12 w-12 items-center justify-center rounded-full"
                style={{ backgroundColor: `${room.accent}26` }}
              >
                <PawPrint
                  className="h-5 w-5"
                  style={{ color: room.accent }}
                  fill={room.accent}
                  strokeWidth={0}
                />
              </span>
              <p className="mt-6 text-[10.5px] font-semibold uppercase tracking-[0.26em] text-ink/55">
                We&rsquo;re building something special here.
              </p>
              <DialogTitle className="mt-3 font-serif text-[32px] font-medium leading-tight tracking-tight text-ink">
                {room.name} is coming soon.
              </DialogTitle>
              <DialogDescription className="mt-3 text-sm leading-relaxed text-ink/70">
                Enter your email and we&rsquo;ll notify you when {room.name} is ready.
              </DialogDescription>

              <form onSubmit={handleSubmit} className="mt-7" noValidate>
                <label htmlFor="notify-email" className="sr-only">
                  Email address
                </label>
                <input
                  id="notify-email"
                  data-testid="email-input"
                  type="email"
                  autoComplete="email"
                  inputMode="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  aria-invalid={Boolean(error)}
                  aria-describedby={error ? "notify-error" : undefined}
                  className="h-[52px] w-full rounded-full border border-ink/15 bg-white/70 px-6 text-[15px] text-ink placeholder:text-ink/35 focus:border-ink/40 focus:outline-none"
                />
                {error && (
                  <p
                    id="notify-error"
                    role="alert"
                    data-testid="form-error"
                    className="mt-3 px-2 text-[13px] font-medium text-[#A03D2A]"
                  >
                    {error}
                  </p>
                )}
                <button
                  type="submit"
                  data-testid="notify-button"
                  disabled={loading}
                  className="mt-4 flex h-[52px] w-full items-center justify-center gap-2 rounded-full text-[13px] font-semibold uppercase tracking-[0.18em] text-ivory transition-transform duration-300 hover:scale-[1.02] active:scale-[0.98] disabled:opacity-70"
                  style={{ backgroundColor: room.accent }}
                >
                  {loading && <Loader2 className="h-4 w-4 animate-spin" />}
                  Notify Me
                </button>
              </form>
              <p className="mt-5 text-center text-[11px] leading-relaxed text-ink/45">
                We&rsquo;ll only use this email to keep you updated about My
                Dachshund Days.
              </p>
            </motion.div>
          )}
        </AnimatePresence>
      </DialogContent>
    </Dialog>
  );
};
